import { X, Sparkles, Lightbulb, Save, RotateCcw, FolderOpen, Trash2, Edit2, Check } from 'lucide-react';
import { useState, useEffect } from 'react';

interface SettingsPanelProps {
  onClose: () => void;
  onPromptUpdate?: (generatePrompt: string, promptifyPrompt: string) => void;
}

interface SavedPrompt {
  id: string;
  name: string;
  generatePrompt: string;
  promptifyPrompt: string;
  timestamp: string;
}

// Prompt DEFAULT per "Genera" (dal backend flow_generator.py)
const DEFAULT_GENERATE_SYSTEM_PROMPT = `Sei un esperto di conversational design e flow automation.

Il tuo compito è generare un flow JSON in formato OYSTAR Flow v1.0 da una descrizione in linguaggio naturale.

# FORMATO JSON OYSTAR FLOW v1.0

\`\`\`json
{
  "id": "flow-name",
  "version": "1.0",
  "metadata": {
    "created": "2025-10-26T00:00:00.000Z",
    "engine": "LLM Generated",
    "format": "pigna-flow-v1"
  },
  "nodes": [
    {
      "id": "start-1",
      "type": "start",
      "position": { "x": 100, "y": 100 },
      "data": { "label": "Start", "message": "Messaggio iniziale" }
    },
    {
      "id": "input-2",
      "type": "input",
      "position": { "x": 100, "y": 200 },
      "data": { 
        "label": "Chiedi Info",
        "prompt": "Domanda per l'utente",
        "variableName": "nome_variabile"
      }
    },
    {
      "id": "say-3",
      "type": "say",
      "position": { "x": 100, "y": 300 },
      "data": { "message": "Testo. Usa {{variabile}} per variabili." }
    },
    {
      "id": "condition-4",
      "type": "condition",
      "position": { "x": 100, "y": 400 },
      "data": { 
        "variable": "nome_variabile",
        "value": "valore_confronto"
      }
    },
    {
      "id": "case-5",
      "type": "case",
      "position": { "x": 100, "y": 500 },
      "data": { 
        "variable": "nome_variabile",
        "cases": [
          { "label": "Opzione 1", "value": "valore1" },
          { "label": "Opzione 2", "value": "valore2" }
        ]
      }
    },
    {
      "id": "end-6",
      "type": "end",
      "position": { "x": 100, "y": 600 },
      "data": { "message": "Fine conversazione" }
    }
  ],
  "edges": [
    { "id": "e1", "source": "start-1", "target": "input-2" },
    { "id": "e2", "source": "input-2", "target": "say-3" },
    { 
      "id": "e3", 
      "source": "condition-4", 
      "target": "say-yes",
      "label": "T",
      "style": { "stroke": "#10b981", "strokeWidth": 2 }
    },
    { 
      "id": "e4", 
      "source": "condition-4", 
      "target": "say-no",
      "label": "F",
      "style": { "stroke": "#ef4444", "strokeWidth": 2 }
    }
  ]
}
\`\`\`

# REGOLE OBBLIGATORIE:

1. **SEMPRE** inizia con un nodo "start"
2. **SEMPRE** termina con un nodo "end"
3. Usa "input" per raccogliere dati dall'utente
4. Usa "say" per messaggi del bot
5. Usa "condition" per decisioni binarie (2 edge: primo=TRUE con label "T" verde, secondo=FALSE con label "F" rosso)
6. Usa "case" per decisioni multiple (N edge con label personalizzate e colori arcobaleno)
7. Le variabili si salvano con "input" (campo variableName)
8. Le variabili si usano con {{nome_variabile}} nei messaggi
9. Ogni nodo deve avere un id univoco (tipo-numero)
10. Position: distribuisci nodes vertically (y incrementa di 100-150 per nodo)
11. **RISPONDI SOLO con JSON valido, niente altro testo**

Poi l'utente inserisce la sua descrizione e l'LLM genera il flow JSON.`;

// Prompt DEFAULT per "Lampadina" (Promptify)
const DEFAULT_PROMPTIFY_SYSTEM_PROMPT = `Sei un Prompt Engineer per OYSTAR Flow Studio.
Dato un OBIETTIVO in linguaggio naturale, produci una DESCRIZIONE OPERATIVA concisa e completa, pronta da incollare nel campo "Genera".
- In ITALIANO, senza preamboli.
- Definisci: Scopo, Nodi (start, say, input, rag, condition, case, action, end), Logica e rami, Messaggi esatti, RAG (se serve: enabled, indexNames, topK, query template), Criteri di chiusura.
- Evita JSON/markup; sezioni testuali.
- Dai stringhe pronte per messaggi e condizioni.
Output: SOLO la descrizione.

Esempio di input utente:
OBIETTIVO: Creare un assistente per prenotazioni ristoranti

Esempio di output:
Scopo: Assistente per prenotare tavolo al ristorante

Nodi:
- start: Saluta e chiedi nome
- input: Raccolta nome (variabile: user_name)
- say: Conferma nome
- input: Quante persone? (variabile: num_persone)
- input: Che giorno? (variabile: data_prenotazione)
- say: Conferma prenotazione con tutti i dati
- end: Ringrazia

Logica:
Flusso lineare senza condizioni

Messaggi:
- Start: "Benvenuto al Ristorante La Luna! Come ti chiami?"
- Conferma nome: "Ciao {{user_name}}!"
- ...ecc`;

export default function SettingsPanel({ onClose, onPromptUpdate }: SettingsPanelProps) {
  // State per i prompt modificabili
  const [generatePrompt, setGeneratePrompt] = useState('');
  const [promptifyPrompt, setPromptifyPrompt] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  
  // State per la libreria di prompt
  const [savedPrompts, setSavedPrompts] = useState<SavedPrompt[]>([]);
  const [activePromptId, setActivePromptId] = useState<string | null>(null);
  const [editingPromptId, setEditingPromptId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');

  // Carica prompt da localStorage all'avvio
  useEffect(() => {
    const savedGeneratePrompt = localStorage.getItem('pigna-flow-generate-prompt');
    const savedPromptifyPrompt = localStorage.getItem('pigna-flow-promptify-prompt');
    const activeId = localStorage.getItem('pigna-flow-active-prompt-id');
    
    setGeneratePrompt(savedGeneratePrompt || DEFAULT_GENERATE_SYSTEM_PROMPT);
    setPromptifyPrompt(savedPromptifyPrompt || DEFAULT_PROMPTIFY_SYSTEM_PROMPT);
    setActivePromptId(activeId);
    
    // Carica libreria prompt salvati
    loadSavedPrompts();
  }, []);
  
  const loadSavedPrompts = () => {
    const saved = localStorage.getItem('pigna-flow-prompt-library');
    if (saved) {
      try {
        const library = JSON.parse(saved);
        setSavedPrompts(library);
      } catch (e) {
        console.error('Errore caricamento libreria prompt:', e);
        setSavedPrompts([]);
      }
    }
  };

  // Controlla se ci sono modifiche non salvate
  useEffect(() => {
    const savedGeneratePrompt = localStorage.getItem('pigna-flow-generate-prompt') || DEFAULT_GENERATE_SYSTEM_PROMPT;
    const savedPromptifyPrompt = localStorage.getItem('pigna-flow-promptify-prompt') || DEFAULT_PROMPTIFY_SYSTEM_PROMPT;
    
    setHasChanges(
      generatePrompt !== savedGeneratePrompt || 
      promptifyPrompt !== savedPromptifyPrompt
    );
  }, [generatePrompt, promptifyPrompt]);

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      alert(`✅ ${label} copiato negli appunti!`);
    } catch {
      alert('❌ Impossibile copiare');
    }
  };

  const savePrompts = () => {
    localStorage.setItem('pigna-flow-generate-prompt', generatePrompt);
    localStorage.setItem('pigna-flow-promptify-prompt', promptifyPrompt);
    setHasChanges(false);
    
    // Notifica il parent component (FlowStudio) dei nuovi prompt
    if (onPromptUpdate) {
      onPromptUpdate(generatePrompt, promptifyPrompt);
    }
    
    alert('✅ Prompt salvati! Verranno usati per le prossime generazioni.');
  };

  const resetToDefaults = () => {
    if (confirm('⚠️ Vuoi ripristinare i prompt alle impostazioni predefinite?')) {
      setGeneratePrompt(DEFAULT_GENERATE_SYSTEM_PROMPT);
      setPromptifyPrompt(DEFAULT_PROMPTIFY_SYSTEM_PROMPT);
      setActivePromptId(null);
      localStorage.removeItem('pigna-flow-generate-prompt');
      localStorage.removeItem('pigna-flow-promptify-prompt');
      localStorage.removeItem('pigna-flow-active-prompt-id');
      
      if (onPromptUpdate) {
        onPromptUpdate(DEFAULT_GENERATE_SYSTEM_PROMPT, DEFAULT_PROMPTIFY_SYSTEM_PROMPT);
      }
      
      alert('✅ Prompt ripristinati ai valori predefiniti!');
    }
  };
  
  const savePromptAs = () => {
    const name = prompt('📝 Nome per questo set di prompt:', `Prompt ${savedPrompts.length + 1}`);
    if (!name) return;
    
    const newPrompt: SavedPrompt = {
      id: `prompt-${Date.now()}`,
      name: name.trim(),
      generatePrompt,
      promptifyPrompt,
      timestamp: new Date().toISOString()
    };
    
    const updatedLibrary = [...savedPrompts, newPrompt];
    setSavedPrompts(updatedLibrary);
    localStorage.setItem('pigna-flow-prompt-library', JSON.stringify(updatedLibrary));
    setActivePromptId(newPrompt.id);
    localStorage.setItem('pigna-flow-active-prompt-id', newPrompt.id);
    
    alert(`✅ Prompt "${name}" salvato nella libreria!`);
  };
  
  const loadPrompt = (prompt: SavedPrompt) => {
    if (hasChanges) {
      if (!confirm('⚠️ Hai modifiche non salvate. Vuoi caricare questo prompt comunque?')) {
        return;
      }
    }
    
    setGeneratePrompt(prompt.generatePrompt);
    setPromptifyPrompt(prompt.promptifyPrompt);
    setActivePromptId(prompt.id);
    
    // Salva come corrente
    localStorage.setItem('pigna-flow-generate-prompt', prompt.generatePrompt);
    localStorage.setItem('pigna-flow-promptify-prompt', prompt.promptifyPrompt);
    localStorage.setItem('pigna-flow-active-prompt-id', prompt.id);
    
    if (onPromptUpdate) {
      onPromptUpdate(prompt.generatePrompt, prompt.promptifyPrompt);
    }
    
    alert(`✅ Prompt "${prompt.name}" caricato!`);
  };
  
  const deletePrompt = (promptId: string) => {
    const prompt = savedPrompts.find(p => p.id === promptId);
    if (!prompt) return;
    
    if (!confirm(`⚠️ Vuoi eliminare il prompt "${prompt.name}"?`)) {
      return;
    }
    
    const updatedLibrary = savedPrompts.filter(p => p.id !== promptId);
    setSavedPrompts(updatedLibrary);
    localStorage.setItem('pigna-flow-prompt-library', JSON.stringify(updatedLibrary));
    
    if (activePromptId === promptId) {
      setActivePromptId(null);
      localStorage.removeItem('pigna-flow-active-prompt-id');
    }
    
    alert(`✅ Prompt "${prompt.name}" eliminato!`);
  };
  
  const startEditingName = (prompt: SavedPrompt) => {
    setEditingPromptId(prompt.id);
    setEditingName(prompt.name);
  };
  
  const saveEditedName = (promptId: string) => {
    if (!editingName.trim()) {
      alert('❌ Il nome non può essere vuoto');
      return;
    }
    
    const updatedLibrary = savedPrompts.map(p => 
      p.id === promptId ? { ...p, name: editingName.trim() } : p
    );
    setSavedPrompts(updatedLibrary);
    localStorage.setItem('pigna-flow-prompt-library', JSON.stringify(updatedLibrary));
    setEditingPromptId(null);
    setEditingName('');
  };
  
  const cancelEditingName = () => {
    setEditingPromptId(null);
    setEditingName('');
  };

  return (
    <div className="w-[500px] bg-white border-l-4 border-gray-300 flex flex-col overflow-hidden shadow-2xl">
      {/* Header */}
      <div className="p-6 border-b-2 border-gray-200 bg-gradient-to-r from-purple-50 to-pink-50">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            ⚙️ Impostazioni Prompt
          </h2>
          <button
            onClick={onClose}
            className="bg-gray-500 hover:bg-gray-600 text-white p-2 rounded-lg transition"
            title="Chiudi pannello"
          >
            <X size={20} />
          </button>
        </div>
        
        {/* Action Buttons */}
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={savePrompts}
            disabled={!hasChanges}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition ${
              hasChanges 
                ? 'bg-green-500 hover:bg-green-600 text-white' 
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
            title={hasChanges ? 'Salva modifiche correnti' : 'Nessuna modifica da salvare'}
          >
            <Save size={14} />
            Salva
          </button>
          
          <button
            onClick={savePromptAs}
            className="flex items-center gap-2 px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-xs font-semibold transition"
            title="Salva come nuovo nella libreria"
          >
            <FolderOpen size={14} />
            Salva come...
          </button>
          
          <button
            onClick={resetToDefaults}
            className="flex items-center gap-2 px-3 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-xs font-semibold transition"
            title="Ripristina prompt predefiniti"
          >
            <RotateCcw size={14} />
            Default
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {/* Libreria Prompt Salvati */}
        {savedPrompts.length > 0 && (
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-5 rounded-xl border-2 border-blue-300">
            <h3 className="text-lg font-bold text-blue-900 mb-3 flex items-center gap-2">
              <FolderOpen className="w-5 h-5" />
              📚 Libreria Prompt ({savedPrompts.length})
            </h3>
            
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {savedPrompts.map(prompt => (
                <div 
                  key={prompt.id}
                  className={`bg-white rounded-lg p-3 border-2 transition ${
                    activePromptId === prompt.id 
                      ? 'border-green-500 shadow-md' 
                      : 'border-blue-200 hover:border-blue-400'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    {editingPromptId === prompt.id ? (
                      <>
                        <input
                          type="text"
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          className="flex-1 px-2 py-1 border-2 border-blue-300 rounded text-sm font-semibold"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveEditedName(prompt.id);
                            if (e.key === 'Escape') cancelEditingName();
                          }}
                        />
                        <button
                          onClick={() => saveEditedName(prompt.id)}
                          className="p-1.5 bg-green-500 hover:bg-green-600 text-white rounded transition"
                          title="Salva nome"
                        >
                          <Check size={14} />
                        </button>
                        <button
                          onClick={cancelEditingName}
                          className="p-1.5 bg-gray-400 hover:bg-gray-500 text-white rounded transition"
                          title="Annulla"
                        >
                          <X size={14} />
                        </button>
                      </>
                    ) : (
                      <>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-bold text-gray-900">
                              {prompt.name}
                            </span>
                            {activePromptId === prompt.id && (
                              <span className="px-2 py-0.5 bg-green-500 text-white text-xs font-bold rounded">
                                ATTIVO
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {new Date(prompt.timestamp).toLocaleString('it-IT')}
                          </div>
                        </div>
                        
                        <button
                          onClick={() => loadPrompt(prompt)}
                          className="px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-xs font-semibold rounded transition"
                          title="Carica questo prompt"
                        >
                          Carica
                        </button>
                        
                        <button
                          onClick={() => startEditingName(prompt)}
                          className="p-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded transition"
                          title="Rinomina"
                        >
                          <Edit2 size={14} />
                        </button>
                        
                        <button
                          onClick={() => deletePrompt(prompt.id)}
                          className="p-1.5 bg-red-500 hover:bg-red-600 text-white rounded transition"
                          title="Elimina"
                        >
                          <Trash2 size={14} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-3 text-xs text-blue-700 bg-blue-100 p-2 rounded border border-blue-200">
              💡 <strong>Tip:</strong> Modifica i prompt qui sotto, poi clicca "Salva come..." per aggiungerli alla libreria
            </div>
          </div>
        )}

        {/* Prompt per Genera */}
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-5 rounded-xl border-2 border-purple-300">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              <h3 className="text-lg font-bold text-purple-900">✨ Genera Flow (LLM)</h3>
            </div>
            <button
              onClick={() => copyToClipboard(generatePrompt, 'Prompt Genera')}
              className="px-3 py-1.5 bg-purple-500 hover:bg-purple-600 text-white text-xs font-semibold rounded-lg transition"
            >
              Copia
            </button>
          </div>
          
          <p className="text-sm text-purple-800 mb-3">
            Questo è il system prompt inviato all'LLM quando clicchi <strong>"✨ Genera"</strong> per creare un flow da una descrizione testuale. <strong>Puoi modificarlo!</strong>
          </p>

          <div className="bg-white rounded-lg border-2 border-purple-200 p-2">
            <textarea
              value={generatePrompt}
              onChange={(e) => setGeneratePrompt(e.target.value)}
              className="w-full h-96 p-3 text-xs font-mono text-gray-700 resize-none focus:outline-none focus:ring-2 focus:ring-purple-400 rounded"
              placeholder="Inserisci il prompt per la generazione del flow..."
              spellCheck={false}
            />
          </div>

          <div className="mt-3 text-xs text-purple-700 bg-purple-100 p-2 rounded border border-purple-200">
            <strong>🎯 Endpoint:</strong> <code className="bg-purple-200 px-1 rounded">POST /flow/generate</code><br/>
            <strong>🤖 Model:</strong> Groq (openai/gpt-oss-120b o llama-3.1-70b-versatile)<br/>
            <strong>📄 File:</strong> <code className="bg-purple-200 px-1 rounded">backend/llm/flow_generator.py</code>
          </div>
        </div>

        {/* Prompt per Lampadina (Promptify) */}
        <div className="bg-gradient-to-br from-amber-50 to-yellow-50 p-5 rounded-xl border-2 border-amber-300">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-amber-600" />
              <h3 className="text-lg font-bold text-amber-900">💡 Suggerisci Prompt</h3>
            </div>
            <button
              onClick={() => copyToClipboard(promptifyPrompt, 'Prompt Lampadina')}
              className="px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold rounded-lg transition"
            >
              Copia
            </button>
          </div>
          
          <p className="text-sm text-amber-800 mb-3">
            Questo è il system prompt inviato a Groq quando clicchi la <strong>lampadina 💡</strong> per trasformare un obiettivo breve in una descrizione operativa dettagliata. <strong>Puoi modificarlo!</strong>
          </p>

          <div className="bg-white rounded-lg border-2 border-amber-200 p-2">
            <textarea
              value={promptifyPrompt}
              onChange={(e) => setPromptifyPrompt(e.target.value)}
              className="w-full h-64 p-3 text-xs font-mono text-gray-700 resize-none focus:outline-none focus:ring-2 focus:ring-amber-400 rounded"
              placeholder="Inserisci il prompt per Promptify..."
              spellCheck={false}
            />
          </div>

          <div className="mt-3 text-xs text-amber-700 bg-amber-100 p-2 rounded border border-amber-200">
            <strong>🎯 Endpoint:</strong> <code className="bg-amber-200 px-1 rounded">/flow/promptify</code><br/>
            <strong>🤖 Model:</strong> Groq (openai/gpt-oss-120b)<br/>
            <strong>📄 File:</strong> <code className="bg-amber-200 px-1 rounded">src/components/FlowStudio.tsx</code> (riga 633)
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-5 rounded-xl border-2 border-blue-300">
          <h3 className="text-lg font-bold text-blue-900 mb-3">ℹ️ Come funziona</h3>
          
          <div className="space-y-3 text-sm text-blue-800">
            <div>
              <strong>1. ✨ Genera Flow:</strong>
              <p className="ml-4 mt-1">Inserisci una descrizione testuale → L'LLM la trasforma in un flow JSON completo con nodi e collegamenti → Il flow viene caricato nel canvas</p>
            </div>
            
            <div>
              <strong>2. 💡 Suggerisci Prompt (Lampadina):</strong>
              <p className="ml-4 mt-1">Inserisci un obiettivo breve → Groq genera una descrizione operativa dettagliata → Puoi copiarla e usarla con "Genera"</p>
            </div>

            <div className="mt-3 pt-3 border-t border-blue-200">
              <strong>💡 Tip:</strong> Usa la lampadina se non sai bene come descrivere il tuo flow. Ti aiuterà a strutturare meglio la richiesta!
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

